import { useMemo } from 'react';

import { generatePath, matchPath } from 'react-router-dom';

import { useAbility } from '@cast/shared-ability-data-access';

import type {
  BreadcrumbContext,
  BreadcrumbItem,
  Breadcrumbs,
  NormalizedBreadcrumbItem,
  OneClickBreadcrumbOnlyItem,
  OneClickLinkItem,
  OneClickNavItem,
  OneClickParentItem,
} from './types';

const isParentRoute = (item: OneClickNavItem): item is OneClickParentItem =>
  (item as OneClickParentItem).children !== undefined;

const validateRoutePermissions = (
  rules: OneClickLinkItem['rules'],
  can: ReturnType<typeof useAbility>['can']
): boolean =>
  !rules || rules.every(([action, subject]) => can(action, subject));

export const flattenNavItems = (items: OneClickNavItem[]): OneClickLinkItem[] =>
  items.flatMap((item) => (isParentRoute(item) ? item.children : [item]));

type GetVisibleNavItemsArgs = {
  items: OneClickNavItem[];
  can: ReturnType<typeof useAbility>['can'];
};

const getVisibleNavItems = ({
  items,
  can,
}: GetVisibleNavItemsArgs): OneClickNavItem[] => {
  const isVisible = (item: OneClickNavItem): boolean =>
    !('routeOnly' in item && item.routeOnly) &&
    validateRoutePermissions(isParentRoute(item) ? undefined : item.rules, can);

  return items.flatMap((item): OneClickNavItem[] => {
    if (!isVisible(item)) {
      return [];
    }

    if (isParentRoute(item)) {
      const children = item.children.filter(isVisible);
      return children.length > 0 ? [{ ...item, children }] : [];
    }

    return [item];
  });
};

export const useVisibleNavItems = (
  items: OneClickNavItem[]
): OneClickNavItem[] => {
  const { can } = useAbility();
  return useMemo(() => getVisibleNavItems({ items, can }), [can, items]);
};

const resolveBreadcrumbs = (
  breadcrumbs: Breadcrumbs,
  ctx: BreadcrumbContext
): BreadcrumbItem[] | undefined =>
  typeof breadcrumbs === 'function' ? breadcrumbs(ctx) : breadcrumbs;

const resolveMatchedPath = (
  pattern: string,
  params: Record<string, string | undefined>
): string | undefined => {
  try {
    const path = generatePath(
      pattern,
      params as Record<string, string | null | undefined>
    );
    if (path.includes(':') || path.includes('*')) {
      return undefined;
    }
    return path;
  } catch {
    return undefined;
  }
};

const withDefaultLinks = (
  breadcrumbs: BreadcrumbItem[],
  to: string | undefined
): BreadcrumbItem[] => {
  if (!to) {
    return breadcrumbs;
  }

  return breadcrumbs.map((item) => {
    if (typeof item === 'string') {
      return { label: item, to };
    }
    if (item.to) {
      return item;
    }
    return { ...item, to };
  });
};

type MatchBreadcrumbsArgs = {
  pathname: string;
  search?: string;
  basePath?: string;
  items: Array<OneClickLinkItem | OneClickBreadcrumbOnlyItem>;
};

export const matchBreadcrumbs = ({
  pathname,
  search = '',
  basePath,
  items,
}: MatchBreadcrumbsArgs): BreadcrumbItem[] | undefined => {
  for (const item of items) {
    const pattern =
      'matchRoute' in item && item.matchRoute
        ? item.matchRoute
        : basePath && 'route' in item
        ? `${basePath}/${item.route}`.replace(/\/+/g, '/')
        : 'route' in item
        ? item.route
        : undefined;

    if (!pattern) {
      continue;
    }

    const normalized = pattern.replace(/\/\*$/, '');

    const match = matchPath(
      { path: normalized, end: item.end ?? false },
      pathname
    );
    if (!match) {
      continue;
    }

    const resolved = resolveBreadcrumbs(item.breadcrumbs, {
      pathname,
      search,
      params: match.params,
    });

    if (!resolved?.length) {
      return resolved;
    }

    return withDefaultLinks(
      resolved,
      resolveMatchedPath(normalized, match.params)
    );
  }

  return undefined;
};

export const normalizeBreadcrumbItem = (
  item: BreadcrumbItem
): NormalizedBreadcrumbItem =>
  typeof item === 'string' ? { label: item } : item;

export const toPageLabel = (
  breadcrumbs: BreadcrumbItem[] | undefined
): NormalizedBreadcrumbItem[] | undefined => {
  if (!breadcrumbs?.length) {
    return undefined;
  }
  return breadcrumbs.map(normalizeBreadcrumbItem);
};
